import { Icon, IconSize, IconType, Tooltip } from "@dexibit/core";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import type { ActiveLink, NavigationLink, SubLink } from "../../types";
import {
  AccordionToggle,
  NavItemIcon,
  NavItemLabel,
  NavItemRow,
  SubItemList,
  SubItemRow,
} from "./styles";

interface NavItemProps {
  link: NavigationLink;
  activeLink: ActiveLink | undefined;
  isExpanded: boolean;
  isAccordionOpen: boolean;
  onToggleAccordion: () => void;
  onOpenAccordion: () => void;
  onNavigate: (link: NavigationLink | SubLink) => void;
}

export const NavItem = ({
  link,
  activeLink,
  isExpanded,
  isAccordionOpen,
  onToggleAccordion,
  onOpenAccordion,
  onNavigate,
}: NavItemProps) => {
  const navSubLinks = link.subLinks?.filter((sub) => sub.showInNav !== false);
  const hasSubLinks = navSubLinks && navSubLinks.length > 0;
  // A link is "nav-hidden" if it only exists for routing (all sublinks are showInNav: false).
  const isNavHidden = link.subLinks?.length && !hasSubLinks;
  // Compare with activeLink.to by equality, not by prefix. getActiveLink
  // already resolves the longest-prefix winner, so only one NavItem's `to`
  // should match — prefix-matching here would over-highlight parents when
  // their path is a prefix of a sibling (eg. Ask vs Knowledge).
  const isActive =
    link.subLinks?.some((sub) => sub.to === activeLink?.to) ||
    (!link.subLinks?.length && activeLink?.to === link.to);

  const handleRowClick = () => {
    if (hasSubLinks) {
      onToggleAccordion();
    } else {
      onNavigate(link);
    }
  };

  if (isNavHidden) return null;

  const iconContent = (
    <NavItemIcon>
      <Icon type={IconType.fa} icon={link.icon} />
    </NavItemIcon>
  );

  return (
    <div>
      <NavItemRow
        isActive={isActive}
        onClick={handleRowClick}
        data-cy={`${link.title.toLowerCase()}-nav-item`}
        aria-current={isActive ? "page" : undefined}
        aria-expanded={hasSubLinks ? isAccordionOpen : undefined}
        aria-label={hasSubLinks ? `${link.title}, submenu` : undefined}
      >
        {!isExpanded ? (
          <Tooltip title={link.title} placement="right">
            <span style={{ display: "flex" }}>{iconContent}</span>
          </Tooltip>
        ) : (
          iconContent
        )}
        <NavItemLabel isExpanded={isExpanded}>{link.title}</NavItemLabel>
        {hasSubLinks && (
          <AccordionToggle isOpen={isAccordionOpen} isExpanded={isExpanded}>
            <Icon type={IconType.fa} icon={faChevronRight} size={IconSize.xs} />
          </AccordionToggle>
        )}
      </NavItemRow>
      {hasSubLinks && (
        <SubItemList
          isOpen={isAccordionOpen}
          itemCount={navSubLinks.length}
          isExpanded={isExpanded}
          onFocus={onOpenAccordion}
        >
          {navSubLinks.map((sublink) => {
            const isSubActive = sublink.to === activeLink?.to;
            return (
              <SubItemRow
                key={sublink.to}
                isActive={isSubActive}
                onClick={() => onNavigate(sublink)}
                data-cy={`${sublink.title.toLowerCase().replace(/\s+/g, "-")}-sub-item`}
                aria-current={isSubActive ? "page" : undefined}
              >
                {sublink.title}
              </SubItemRow>
            );
          })}
        </SubItemList>
      )}
    </div>
  );
};
